import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import type { AdjustmentLine } from '@/types/invoicing';

const LINE_TYPES = [
  { value: 'discount', label: 'Discount' },
  { value: 'deduction', label: 'Deduction' },
  { value: 'surcharge', label: 'Surcharge' },
  { value: 'adjustment', label: 'Adjustment' },
] as const;

interface AdjustmentLinesEditorProps {
  lines: AdjustmentLine[];
  onChange: (lines: AdjustmentLine[]) => void;
  /** If true, fields are disabled */
  disabled?: boolean;
}

export function AdjustmentLinesEditor({
  lines,
  onChange,
  disabled = false,
}: AdjustmentLinesEditorProps) {
  const addLine = () => {
    onChange([
      ...lines,
      {
        line_type: 'deduction',
        description: '',
        amount: '',
        percentage: null,
        show_on_invoice: true,
        order: lines.length,
      },
    ]);
  };

  const removeLine = (index: number) => {
    onChange(lines.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, field: keyof AdjustmentLine, value: any) => {
    const updated = lines.map((line, i) => {
      if (i !== index) return line;
      const newLine = { ...line, [field]: value };

      // When line_type changes, auto-adjust the sign of amount
      if (field === 'line_type') {
        const amt = parseFloat(String(newLine.amount)) || 0;
        if ((value === 'discount' || value === 'deduction') && amt > 0) {
          newLine.amount = String(-Math.abs(amt));
        } else if ((value === 'surcharge' || value === 'adjustment') && amt < 0) {
          newLine.amount = String(Math.abs(amt));
        }
      }

      return newLine;
    });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">Additional Adjustment Lines</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addLine}
          disabled={disabled}
          className="h-7 text-xs gap-1"
        >
          <Plus className="h-3 w-3" />
          Add Line
        </Button>
      </div>

      {lines.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          No additional adjustment lines. Click "Add Line" to add discounts, deductions, etc.
        </p>
      )}

      {lines.map((line, index) => (
        <div
          key={index}
          className="grid grid-cols-[120px_1fr_100px_32px] gap-2 items-end border rounded-md p-2 bg-muted/30"
        >
          {/* Type */}
          <div>
            <Label className="text-[10px] text-muted-foreground">Type</Label>
            <Select
              value={line.line_type}
              onValueChange={(v) => updateLine(index, 'line_type', v)}
              disabled={disabled}
            >
              <SelectTrigger className="h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LINE_TYPES.map((lt) => (
                  <SelectItem key={lt.value} value={lt.value}>
                    {lt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div>
            <Label className="text-[10px] text-muted-foreground">Description</Label>
            <Input
              className="h-7 text-xs"
              placeholder="e.g. Court case deduction"
              value={line.description}
              onChange={(e) => updateLine(index, 'description', e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Amount */}
          <div>
            <Label className="text-[10px] text-muted-foreground">Amount</Label>
            <Input
              className="h-7 text-xs"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={line.amount}
              onChange={(e) => updateLine(index, 'amount', e.target.value)}
              disabled={disabled}
            />
          </div>

          {/* Remove */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => removeLine(index)}
            disabled={disabled}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>

          {/* Second row: percentage + show on invoice */}
          <div className="col-span-4 flex items-center gap-4 pl-1">
            <div className="flex items-center gap-2">
              <Label className="text-[10px] text-muted-foreground">%</Label>
              <Input
                className="h-6 text-xs w-16"
                type="number"
                step="0.01"
                placeholder="—"
                value={line.percentage ?? ''}
                onChange={(e) =>
                  updateLine(index, 'percentage', e.target.value || null)
                }
                disabled={disabled}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Checkbox
                id={`show-on-invoice-${index}`}
                checked={line.show_on_invoice !== false}
                onCheckedChange={(v) => updateLine(index, 'show_on_invoice', !!v)}
                disabled={disabled}
                className="h-3.5 w-3.5"
              />
              <Label
                htmlFor={`show-on-invoice-${index}`}
                className="text-[10px] text-muted-foreground"
              >
                Show on invoice
              </Label>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
